import { fetchers } from "@/services/api/fetchers";
import { FetchedTag, TagInsert, TagUpdate } from "@/services/api/fetchers/tags";
import { performOptimisticAction } from "@/shared/utils/store";
import { StateCreator } from "zustand";
import { BoundStore } from "./index";

export interface TagsSlice {
  tags: {
    list: FetchedTag[];
    noteTags: Record<number, FetchedTag[]>;
    addNoteTag: (noteId: number, tagId: number) => Promise<void>;
    addTag: (tag: TagInsert) => Promise<FetchedTag | undefined>;
    deleteTag: (id: number) => Promise<void>;
    fetchNoteTags: (noteId: number) => Promise<FetchedTag[]>;
    fetchTags: () => Promise<FetchedTag[]>;
    removeNoteTag: (noteId: number, tagId: number) => Promise<void>;
    updateTag: (id: number, tag: TagUpdate) => Promise<FetchedTag | null>;
  };
}

const createTagsSlice: StateCreator<BoundStore, [], [], TagsSlice> = (set, get) => ({
  tags: {
    list: [],
    noteTags: {},

    fetchTags: async () => {
      try {
        const tags = await fetchers.tags.getTags();
        set((state) => ({
          tags: {
            ...state.tags,
            list: tags.toSorted((a, b) => a.name.localeCompare(b.name)),
          },
        }));
        return tags;
      } catch (error) {
        console.error("Failed to fetch tags:", error);
        return [];
      }
    },

    fetchNoteTags: async (noteId) => {
      try {
        const noteTags = await fetchers.tags.getNoteTags(noteId);
        set((state) => ({
          tags: {
            ...state.tags,
            noteTags: {
              ...state.tags.noteTags,
              [noteId]: noteTags,
            },
          },
        }));
        return noteTags;
      } catch (error) {
        console.error(`Failed to fetch tags for note ${noteId}:`, error);
        return [];
      }
    },

    addTag: async (tag) => {
      const existingTags = get().tags.list;
      const tempId = Date.now();
      const tempTag = { ...tag, id: tempId, name: tag.name };

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const updatedList = [...state.tags.list, tempTag as FetchedTag].sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            return {
              tags: {
                ...state.tags,
                list: updatedList,
              },
            };
          }),
        apiCall: async () => {
          const savedTag = await fetchers.tags.addTag(tag);
          set((state) => ({
            tags: {
              ...state.tags,
              list: state.tags.list
                .map((t) => (t.id === tempId ? savedTag : t))
                .sort((a, b) => a.name.localeCompare(b.name)),
            },
          }));
          return savedTag;
        },
        rollback: () =>
          set((state) => ({
            tags: {
              ...state.tags,
              list: existingTags,
            },
          })),
      });
    },

    updateTag: async (id, tag) => {
      const existingTags = get().tags.list;
      const existingNoteTags = get().tags.noteTags;
      const existingNotes = get().notes.main;
      const tagToUpdate = existingTags.find((t) => t.id === id);

      if (!tagToUpdate) return null;

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            // Update the tag in the main list
            const updatedList = state.tags.list
              .map((t) => (t.id === id ? { ...t, ...tag } : t))
              .sort((a, b) => a.name.localeCompare(b.name));

            // Update the tag in all note-tag associations
            const updatedNoteTags = { ...state.tags.noteTags };
            Object.keys(updatedNoteTags).forEach((noteId) => {
              updatedNoteTags[Number(noteId)] = updatedNoteTags[Number(noteId)].map((t) =>
                t.id === id ? { ...t, ...tag } : t
              );
            });

            // Update the tag in all notes
            const updatedNotes = state.notes.main.map((note) => {
              if (note.tags?.some((t) => t.id === id)) {
                return {
                  ...note,
                  tags: note.tags.map((t) => (t.id === id ? { ...t, ...tag } : t)),
                };
              }
              return note;
            });

            return {
              notes: {
                ...state.notes,
                main: updatedNotes,
              },
              tags: {
                ...state.tags,
                list: updatedList,
                noteTags: updatedNoteTags,
              },
            };
          }),
        apiCall: async () => {
          const updatedTag = await fetchers.tags.updateTag({ id, tag });
          return updatedTag;
        },
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingNotes,
            },
            tags: {
              ...state.tags,
              list: existingTags,
              noteTags: existingNoteTags,
            },
          })),
      });
    },

    deleteTag: async (id) => {
      const existingTags = get().tags.list;
      const existingNoteTags = get().tags.noteTags;
      const existingNotes = get().notes.main;

      await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            // Remove the tag from the main list
            const updatedList = state.tags.list.filter((t) => t.id !== id);

            // Remove the tag from all note-tag associations
            const updatedNoteTags = { ...state.tags.noteTags };
            Object.keys(updatedNoteTags).forEach((noteId) => {
              updatedNoteTags[Number(noteId)] = updatedNoteTags[Number(noteId)].filter((t) => t.id !== id);
            });

            // Remove the tag from all notes
            const updatedNotes = state.notes.main.map((note) => {
              if (note.tags?.some((t) => t.id === id)) {
                return {
                  ...note,
                  tags: note.tags.filter((t) => t.id !== id),
                };
              }
              return note;
            });

            return {
              notes: {
                ...state.notes,
                main: updatedNotes,
              },
              tags: {
                ...state.tags,
                list: updatedList,
                noteTags: updatedNoteTags,
              },
            };
          }),
        apiCall: async () => {
          await fetchers.tags.deleteTag(id);
        },
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingNotes,
            },
            tags: {
              ...state.tags,
              list: existingTags,
              noteTags: existingNoteTags,
            },
          })),
      });
    },

    addNoteTag: async (noteId, tagId) => {
      const existingNoteTags = get().tags.noteTags[noteId] || [];
      const existingNotes = get().notes.main;
      const tagToAdd = get().tags.list.find((t) => t.id === tagId);

      if (!tagToAdd) return;

      await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const updatedNoteTags = {
              ...state.tags.noteTags,
              [noteId]: [...(state.tags.noteTags[noteId] || []), tagToAdd],
            };

            // Update the tag in all notes
            const updatedNotes = state.notes.main.map((note) => {
              if (note.id === noteId) {
                return {
                  ...note,
                  tags: [...note.tags, tagToAdd],
                };
              }
              return note;
            });

            return {
              notes: {
                ...state.notes,
                main: updatedNotes,
              },
              tags: {
                ...state.tags,
                noteTags: updatedNoteTags,
              },
            };
          }),
        apiCall: async () => {
          await fetchers.tags.addNoteTag(noteId, tagId);
        },
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingNotes,
            },
            tags: {
              ...state.tags,
              noteTags: {
                ...state.tags.noteTags,
                [noteId]: existingNoteTags,
              },
            },
          })),
      });
    },

    removeNoteTag: async (noteId, tagId) => {
      const existingNoteTags = get().tags.noteTags[noteId] || [];
      const existingNotes = get().notes.main;

      await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const updatedNoteTags = {
              ...state.tags.noteTags,
              [noteId]: (state.tags.noteTags[noteId] || []).filter((t) => t.id !== tagId),
            };

            // Update the tag in all notes
            const updatedNotes = state.notes.main.map((note) => {
              if (note.id === noteId) {
                return {
                  ...note,
                  tags: (note.tags || []).filter((t) => t.id !== tagId),
                };
              }
              return note;
            });

            return {
              notes: {
                ...state.notes,
                main: updatedNotes,
              },
              tags: {
                ...state.tags,
                noteTags: updatedNoteTags,
              },
            };
          }),
        apiCall: async () => {
          await fetchers.tags.removeNoteTag(noteId, tagId);
        },
        rollback: () =>
          set((state) => ({
            notes: {
              ...state.notes,
              main: existingNotes,
            },
            tags: {
              ...state.tags,
              noteTags: {
                ...state.tags.noteTags,
                [noteId]: existingNoteTags,
              },
            },
          })),
      });
    },
  },
});

export default createTagsSlice;
